// Creating exercises: manual, AI generation, CSV import.
import { HAS_UNLIMITED_AI_CREDITS, SUPABASE_KEY, SUPABASE_URL } from "../config.js";
import { loadAccount } from "../data/account.js";
import { loadAdminStatistics } from "../data/admin.js";
import { canonicalExerciseDescriptor } from "../domain/curriculum.js";
import { t } from "../i18n.js";
import { readableError } from "../lib/api.js";
import { parseStructuredExerciseCSV } from "../lib/csv.js";
import { closeModal, escapeHTML, setBusy, showModal, showToast } from "../lib/dom.js";
import { difficultyDisplay } from "../lib/format.js";
import { currentItalianSchoolYear, schoolYearOptions } from "../lib/school-year.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { adminView } from "../views/admin.js";
import { saveTeacherExercise, teacherExercisesView } from "../views/teacher-exercises.js";
import { teacherClassesView, teacherView } from "../views/teacher.js";

export function showCreditProductsModal() {
  const products = state.creditProducts.map(product => `
    <button class="exercise-card" data-credit-product="${product.id}">
      <span class="exercise-symbol">✦</span>
      <span>
        <h3>${escapeHTML(state.language === "it" ? product.name_it : product.name_en)}</h3>
        <p>${product.credits} ${state.language === "it" ? "crediti AI" : "AI credits"}</p>
      </span>
      <strong>€${(product.price_cents / 100).toFixed(2)}</strong>
    </button>`).join("");
  showModal(`
    <div class="modal-header">
      <h2>${state.language === "it" ? "Crediti BrainMath AI" : "BrainMath AI Credits"}</h2>
      <button class="close-button" data-close-modal>×</button>
    </div>
    <p style="color:var(--muted)">${state.language === "it"
      ? "Il costo viene calcolato dai token realmente usati. La parte prenotata e non utilizzata viene restituita."
      : "Cost is calculated from the tokens actually used. Reserved but unused credits are returned."}</p>
    <div class="exercise-list">${products}</div>
    <p class="form-message" id="credit-purchase-message"></p>`);
  document.querySelectorAll("[data-credit-product]").forEach(button => {
    button.addEventListener("click", async () => {
      const message = document.querySelector("#credit-purchase-message");
      button.disabled = true;
      const { data: sessionData } = await supabase.auth.getSession();
      try {
        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-credit-checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": SUPABASE_KEY,
            "Authorization": `Bearer ${sessionData.session?.access_token || ""}`
          },
          body: JSON.stringify({ product_id: button.dataset.creditProduct })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.checkout_url) {
          message.textContent = data.message || "Unable to start checkout.";
          button.disabled = false;
          return;
        }
        location.href = data.checkout_url;
      } catch {
        message.textContent = state.language === "it"
          ? "Impossibile avviare il pagamento."
          : "Unable to start payment.";
        button.disabled = false;
      }
    });
  });
}

export function createSchoolModal() {
  showModal(`<div class="modal-header"><h2>${state.language === "it" ? "Aggiungi scuola" : "Add school"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="create-school-form" class="form-grid">
      <label>${state.language === "it" ? "Nome scuola" : "School name"}<input name="name" minlength="3" required></label>
      <label>${state.language === "it" ? "Comune" : "City"}<input name="city" minlength="2" required></label>
      <label>${state.language === "it" ? "Regione" : "Region"}<input name="region" placeholder="${state.language === "it" ? "Es. Lombardia" : "E.g. Lombardy"}"></label>
      <label>${t("country")}<input name="country" value="IT" maxlength="2" required></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Aggiungi scuola" : "Add school"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#create-school-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const values = new FormData(form);
    const { error } = await supabase.rpc("create_teacher_school", {
      school_name: values.get("name").trim(),
      school_city: values.get("city").trim(),
      school_country_code: values.get("country").trim().toUpperCase(),
      school_region: values.get("region").trim()
    });
    if (error) {
      form.querySelector(".form-message").textContent = error.message;
      return;
    }
    await loadAccount();
    closeModal();
    if (state.teacherMode && state.view === "classes") {
      state.teacherOrgTab = "schools";
      teacherClassesView();
    } else {
      teacherView();
    }
  });
}

export function createClassModal() {
  const currentYear = currentItalianSchoolYear();
  showModal(`<div class="modal-header"><h2>${state.language === "it" ? "Nuova classe" : "New class"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="create-class-form" class="form-grid">
      <label>${state.language === "it" ? "Scuola" : "School"}<select name="schoolID" required>
        <option value="">${state.language === "it" ? "Seleziona scuola" : "Select school"}</option>
        ${state.teacherSchools.map(school => `<option value="${school.school_id}">${escapeHTML(school.school_name)} — ${escapeHTML(school.school_city)}</option>`).join("")}
      </select></label>
      <div class="form-row">
        <label>${state.language === "it" ? "Anno/classe" : "Grade"}<input name="grade" type="number" min="1" max="13" value="1" required></label>
        <label>${state.language === "it" ? "Sezione" : "Section"}<input name="section" maxlength="8" value="A" required></label>
      </div>
      <label>${state.language === "it" ? "Anno scolastico" : "School year"}<select name="academicYear" required>${schoolYearOptions(currentYear)}</select></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Crea classe" : "Create class"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#create-class-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const values = new FormData(form);
    const { error } = await supabase.rpc("create_school_class", {
      selected_school: values.get("schoolID"),
      selected_grade: Number(values.get("grade")),
      selected_section: values.get("section").trim(),
      selected_academic_year_id: Number(values.get("academicYear"))
    });
    if (error) {
      form.querySelector(".form-message").textContent = error.message;
      return;
    }
    await loadAccount();
    closeModal();
    if (state.teacherMode && state.view === "classes") {
      state.teacherOrgTab = "classes";
      teacherClassesView();
    } else {
      teacherView();
    }
  });
}

export function getAIGenerationTopicOptions(classID) {
  const classroom = state.schoolClasses.find(item => item.class_id === classID);
  if (!classroom) return [];
  const grade = Number(classroom.grade_level || 0);
  const exerciseCounts = new Map();
  state.teacherCatalogExercises.forEach(exercise => {
    const topicCode = canonicalExerciseDescriptor(exercise).topicCode;
    if (topicCode) exerciseCounts.set(topicCode, (exerciseCounts.get(topicCode) || 0) + 1);
  });
  return state.learningHierarchy
    .filter(topic =>
      grade >= Number(topic.min_grade) && grade <= Number(topic.max_grade)
    )
    .map(topic => ({
      topicCode: topic.learning_topic_code,
      topicName: topic.learning_topic_name,
      macroCategoryName: topic.area_name,
      exerciseCount: exerciseCounts.get(topic.learning_topic_code) || 0,
      macroSort: Number(topic.area_sort || 0),
      topicSort: Number(topic.learning_topic_sort || 0)
    }))
    .sort((a, b) => {
    if (a.macroSort !== b.macroSort) return a.macroSort - b.macroSort;
    if (a.topicSort !== b.topicSort) return a.topicSort - b.topicSort;
    if (a.macroCategoryName !== b.macroCategoryName) {
      return a.macroCategoryName.localeCompare(b.macroCategoryName);
    }
    return a.topicName.localeCompare(b.topicName);
  });
}

export function generateAIExerciseModal() {
  const teacherClasses = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  showModal(`<div class="modal-header"><h2>BrainMath AI</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="generate-ai-form" class="form-grid">
      <label>${state.language === "it" ? "Classe" : "Class"}<select name="classID" required>
        <option value="">${state.language === "it" ? "Seleziona classe" : "Select class"}</option>
        ${teacherClasses.map(classroom => `<option value="${classroom.class_id}">${escapeHTML(classroom.school_name)} · ${escapeHTML(classroom.class_name)} · ${escapeHTML(classroom.academic_year_name)}</option>`).join("")}
      </select></label>
      <div>
        <p style="margin:0 0 10px;color:var(--muted);font-size:13px">${state.language === "it"
          ? "Seleziona fino a 2 argomenti. Gli argomenti coincidono con la gerarchia del catalogo admin e sono filtrati sulla classe scelta."
          : "Select up to 2 topics. Topics match the admin catalogue hierarchy and are filtered to the chosen class."}</p>
        <div id="ai-topic-selector" class="exercise-list"></div>
        <p id="ai-topic-summary" style="margin:10px 0 0;color:var(--muted);font-size:13px"></p>
      </div>
      <section class="ai-only-option">
        <div>
          <span class="eyebrow">${state.language === "it" ? "Opzione AI" : "AI option"}</span>
          <h3>${state.language === "it" ? "Problemi illustrati" : "Illustrated problems"}</h3>
          <p>${state.language === "it"
            ? "Questa modalità sarà gestita solo dall’AI perché richiede immagini contestuali generate automaticamente. Non è disponibile come categoria manuale dell’insegnante."
            : "This mode will be managed only by AI because it requires automatically generated contextual images. It is not available as a manual teacher category."}</p>
        </div>
        <span class="status-pill">${state.language === "it" ? "AI immagini" : "AI images"}</span>
      </section>
      <label>${state.language === "it" ? "Indicazioni extra" : "Extra guidance"}<textarea name="prompt" rows="4" placeholder="${state.language === "it" ? "Facoltativo: aggiungi un vincolo o una spiegazione" : "Optional: add a constraint or detail"}"></textarea></label>
      <label>${state.language === "it" ? "Domande per fascia" : "Questions per band"}<input name="count" type="number" min="1" max="10" value="5" required></label>
      <p style="margin:0;color:var(--muted);font-size:13px">${state.language === "it"
        ? "Ogni argomento genera 4 attività: Base, Medio, Avanzato ed Esperto, con massimo 10 domande per fascia."
        : "Each topic generates 4 activities: Basic, Intermediate, Advanced and Expert, with up to 10 questions per band."}</p>
      ${HAS_UNLIMITED_AI_CREDITS ? `<p style="margin:0;color:var(--turquoise);font-size:13px;font-weight:800">${state.language === "it"
        ? "Beta privata: generazione AI illimitata, senza addebito di crediti."
        : "Private beta: unlimited AI generation, with no credits charged."}</p>` : ""}
      <div id="ai-credit-estimate" class="stat" style="display:none"></div>
      <button class="primary full" type="submit">${state.language === "it" ? "Genera bozza" : "Generate draft"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#generate-ai-form");
  const classSelect = form.querySelector('select[name="classID"]');
  const topicContainer = form.querySelector("#ai-topic-selector");
  const topicSummary = form.querySelector("#ai-topic-summary");
  const countInput = form.querySelector('input[name="count"]');
  const creditEstimate = form.querySelector("#ai-credit-estimate");
  const submitButton = form.querySelector('button[type="submit"]');
  const selectedTopics = new Set();

  const updateCreditEstimate = () => {
    if (!selectedTopics.size) {
      creditEstimate.style.display = "none";
      submitButton.disabled = false;
      return;
    }
    const difficultyBandCount = 4;
    const questionsPerBand = Math.min(10, Math.max(1, Number(countInput.value) || 1));
    const estimatedCredits = Math.max(5, Math.ceil((questionsPerBand * difficultyBandCount * selectedTopics.size) / 10) * 2);
    if (HAS_UNLIMITED_AI_CREDITS) {
      creditEstimate.style.display = "";
      creditEstimate.innerHTML = `<strong>∞</strong><span>${state.language === "it"
        ? `beta privata · ${estimatedCredits} crediti stimati non addebitati`
        : `private beta · ${estimatedCredits} estimated credits not charged`}</span>`;
      submitButton.disabled = false;
      return;
    }
    const availableCredits = Number(state.aiWallet?.balance || 0);
    const enough = availableCredits >= estimatedCredits;
    let maximumQuestions = 0;
    if (availableCredits >= 5) {
      for (let candidate = 1; candidate <= 10; candidate += 1) {
        const candidateCredits = Math.max(5, Math.ceil((candidate * difficultyBandCount * selectedTopics.size) / 10) * 2);
        if (candidateCredits <= availableCredits) maximumQuestions = candidate;
      }
    }
    creditEstimate.style.display = "";
    creditEstimate.innerHTML = `<strong>${estimatedCredits}</strong><span>${state.language === "it"
      ? `crediti stimati · ${availableCredits} disponibili`
      : `estimated credits · ${availableCredits} available`}</span>${enough ? "" : `<small style="color:var(--coral)">${maximumQuestions > 0
        ? (state.language === "it"
            ? `Riduci a massimo ${maximumQuestions} domande per fascia o seleziona un solo argomento.`
            : `Reduce to at most ${maximumQuestions} questions per band or select one topic.`)
        : (state.language === "it"
            ? "Saldo insufficiente anche per la richiesta minima."
            : "The balance is insufficient even for the minimum request.")}</small>`}`;
    submitButton.disabled = !enough;
  };

  const renderTopics = () => {
    const classID = classSelect.value;
    const options = classID ? getAIGenerationTopicOptions(classID) : [];
    if (!classID) {
      topicContainer.innerHTML = `<p style="margin:0;color:var(--muted);font-size:13px">${state.language === "it" ? "Seleziona una classe per vedere gli argomenti." : "Select a class to see the available topics."}</p>`;
      topicSummary.textContent = "";
      updateCreditEstimate();
      return;
    }
    if (!options.length) {
      topicContainer.innerHTML = `<p style="margin:0;color:var(--muted);font-size:13px">${state.language === "it" ? "Non ci sono argomenti compatibili con questa classe." : "There are no topics compatible with this class."}</p>`;
      topicSummary.textContent = "";
      updateCreditEstimate();
      return;
    }
    const grouped = new Map();
    options.forEach(option => {
      const key = option.macroCategoryName;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(option);
    });
    topicContainer.innerHTML = [...grouped.entries()].map(([key, items]) => {
      const macroCategoryName = key;
      return `<div style="margin-bottom:12px">
        <h4 style="margin:0 0 8px">${escapeHTML(macroCategoryName)}</h4>
        <div class="exercise-list">
          ${items.map(option => {
            const selected = selectedTopics.has(option.topicCode);
            const locked = !selected && selectedTopics.size >= 2;
            return `<button type="button" class="exercise-card" data-topic-code="${escapeHTML(option.topicCode)}" ${locked ? "disabled" : ""} style="text-align:left;${selected ? "border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb,var(--accent) 25%,transparent);" : ""}">
              <span class="exercise-symbol">${selected ? "✓" : "●"}</span>
              <span>
                <h3>${escapeHTML(option.topicName)}</h3>
                <p>${option.exerciseCount} ${state.language === "it" ? "attività disponibili" : "available activities"}</p>
              </span>
            </button>`;
          }).join("")}
        </div>
      </div>`;
    }).join("");
    topicSummary.textContent = selectedTopics.size
      ? (state.language === "it" ? `Selezionati ${selectedTopics.size}/2 argomenti.` : `${selectedTopics.size}/2 topics selected.`)
      : (state.language === "it" ? "Nessun argomento selezionato." : "No topic selected.");
    updateCreditEstimate();
  };

  classSelect.addEventListener("change", () => {
    selectedTopics.clear();
    renderTopics();
  });

  topicContainer.addEventListener("click", event => {
    const button = event.target.closest("[data-topic-code]");
    if (!button || button.disabled) return;
    const topicCode = button.dataset.topicCode;
    if (selectedTopics.has(topicCode)) {
      selectedTopics.delete(topicCode);
    } else if (selectedTopics.size < 2) {
      selectedTopics.add(topicCode);
    }
    renderTopics();
  });
  countInput.addEventListener("input", updateCreditEstimate);

  renderTopics();

  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = submitButton;
    const values = new FormData(form);
    if (!selectedTopics.size) {
      form.querySelector(".form-message").textContent = state.language === "it"
        ? "Seleziona almeno un argomento."
        : "Select at least one topic.";
      return;
    }
    setBusy(button, true);
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    let data;
    let errorMessage;
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-exercises`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_KEY,
          "Authorization": `Bearer ${accessToken || ""}`
        },
        body: JSON.stringify({
          prompt: (values.get("prompt") || "").trim(),
          class_id: values.get("classID"),
          question_count: Number(values.get("count")),
          set_count: selectedTopics.size,
          selected_topic_codes: [...selectedTopics],
          dev_unlimited_ai_credits: HAS_UNLIMITED_AI_CREDITS,
          draft_only: true,
          language: state.language
        })
      });
      data = await response.json().catch(() => ({}));
      if (!response.ok) {
        errorMessage = data?.message || `AI service error (${response.status}).`;
      }
    } catch {
      errorMessage = state.language === "it"
        ? "Impossibile contattare il servizio AI. Controlla la connessione e riprova."
        : "Unable to reach the AI service. Check your connection and retry.";
    }
    setBusy(button, false);
    if (errorMessage) {
      form.querySelector(".form-message").textContent = errorMessage;
      return;
    }
    closeModal();
    reviewGeneratedAIDraftModal(data, values.get("classID"));
  });
}

function reviewGeneratedAIDraftModal(data, classID) {
  const exercises = Array.isArray(data?.exercises) ? data.exercises : [];
  if (!exercises.length) {
    showToast(state.language === "it" ? "La bozza AI non contiene esercizi." : "The AI draft has no exercises.");
    return;
  }
  const locale = state.language === "it" ? "it" : "en";
  const exercisePanels = exercises.map((exercise, exerciseIndex) => {
    const localized = exercise[locale] || exercise;
    const questions = Array.isArray(localized.questions) ? localized.questions : [];
    return `<section class="ai-review-exercise">
      <div class="library-heading">
        <div>
          <span class="eyebrow">${difficultyDisplay(exercise.difficulty_level)}</span>
          <h3>${escapeHTML(localized.title || exercise.title || "")}</h3>
          <p>${escapeHTML(localized.topic || exercise.topic || "")}</p>
        </div>
      </div>
      <input type="hidden" name="exercise_${exerciseIndex}_difficulty" value="${Number(exercise.difficulty_level || 1)}">
      <label>${state.language === "it" ? "Titolo esercizio" : "Exercise title"}<input name="exercise_${exerciseIndex}_title" value="${escapeHTML(localized.title || exercise.title || "")}" required></label>
      <label>${state.language === "it" ? "Titolo tematico" : "Topic title"}<input name="exercise_${exerciseIndex}_topic" value="${escapeHTML(localized.topic || exercise.topic || "")}" required></label>
      <div class="question-details">
        ${questions.map((question, questionIndex) => {
          const answers = Array.isArray(question.answers) ? question.answers.slice(0, 4) : ["", "", "", ""];
          while (answers.length < 4) answers.push("");
          const correctIndex = Math.max(0, answers.findIndex(answer => answer === question.correct_answer));
          return `<details class="question-detail" ${questionIndex === 0 ? "open" : ""}>
            <summary><strong>${state.language === "it" ? "Domanda" : "Question"} ${questionIndex + 1}</strong></summary>
            <div class="question-detail-body form-grid">
              <label>${state.language === "it" ? "Testo domanda/problema" : "Question/problem text"}<textarea name="exercise_${exerciseIndex}_question_${questionIndex}_prompt" rows="3" required>${escapeHTML(question.prompt || "")}</textarea></label>
              <div class="form-row">
                ${answers.map((answer, answerIndex) => `
                  <label>${state.language === "it" ? "Risposta" : "Answer"} ${answerIndex + 1}<input name="exercise_${exerciseIndex}_question_${questionIndex}_answer_${answerIndex}" value="${escapeHTML(answer)}" required></label>
                `).join("")}
              </div>
              <label>${state.language === "it" ? "Risposta corretta" : "Correct answer"}<select name="exercise_${exerciseIndex}_question_${questionIndex}_correct" required>
                ${answers.map((_, answerIndex) => `<option value="${answerIndex}" ${answerIndex === correctIndex ? "selected" : ""}>${state.language === "it" ? "Risposta" : "Answer"} ${answerIndex + 1}</option>`).join("")}
              </select></label>
              <label>${state.language === "it" ? "Suggerimento" : "Hint"}<textarea name="exercise_${exerciseIndex}_question_${questionIndex}_hint" rows="2" required>${escapeHTML(question.hint || "")}</textarea></label>
              <label>${state.language === "it" ? "Spiegazione" : "Explanation"}<textarea name="exercise_${exerciseIndex}_question_${questionIndex}_explanation" rows="3" required>${escapeHTML(question.explanation || "")}</textarea></label>
            </div>
          </details>`;
        }).join("")}
      </div>
    </section>`;
  }).join("");
  showModal(`
    <div class="modal-header">
      <div>
        <span class="eyebrow">${state.language === "it" ? "Revisione AI" : "AI review"}</span>
        <h2>${state.language === "it" ? "Controlla prima di assegnare" : "Review before assigning"}</h2>
      </div>
      <button class="close-button" data-close-modal>×</button>
    </div>
    <form id="ai-review-form" class="form-grid">
      <p class="feedback" style="margin-top:0">${state.language === "it"
        ? `La bozza è stata generata. Controlla domande, risposte, suggerimenti e spiegazioni; quando confermi verrà assegnata alla classe. ${data.unlimited_ai_credits || data.dev_unlimited_ai_credits ? "Modalità beta: nessun credito addebitato." : `Crediti generazione: ${data.charged_credits ?? 0}.`}`
        : `The draft was generated. Review questions, answers, hints and explanations; when you confirm it will be assigned to the class. ${data.unlimited_ai_credits || data.dev_unlimited_ai_credits ? "Beta mode: no credits charged." : `Generation credits: ${data.charged_credits ?? 0}.`}`}</p>
      ${exercisePanels}
      <button class="primary full" type="submit">${state.language === "it" ? "Conferma e assegna alla classe" : "Confirm and assign to class"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#ai-review-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    const values = new FormData(form);
    setBusy(button, true);
    try {
      for (let exerciseIndex = 0; exerciseIndex < exercises.length; exerciseIndex += 1) {
        const draft = exercises[exerciseIndex];
        const sourceLocale = draft[locale] || draft;
        const sourceQuestions = Array.isArray(sourceLocale.questions) ? sourceLocale.questions : [];
        const editedQuestions = sourceQuestions.map((_, questionIndex) => {
          const answers = [0, 1, 2, 3].map(answerIndex =>
            String(values.get(`exercise_${exerciseIndex}_question_${questionIndex}_answer_${answerIndex}`) || "").trim()
          );
          const correctIndex = Number(values.get(`exercise_${exerciseIndex}_question_${questionIndex}_correct`) || 0);
          return {
            prompt: String(values.get(`exercise_${exerciseIndex}_question_${questionIndex}_prompt`) || "").trim(),
            answers,
            correct_answer: answers[correctIndex] || answers[0],
            hint: String(values.get(`exercise_${exerciseIndex}_question_${questionIndex}_hint`) || "").trim(),
            explanation: String(values.get(`exercise_${exerciseIndex}_question_${questionIndex}_explanation`) || "").trim(),
          };
        });
        const editedTitle = String(values.get(`exercise_${exerciseIndex}_title`) || "").trim();
        const editedTopic = String(values.get(`exercise_${exerciseIndex}_topic`) || "").trim();
        await saveTeacherExercise({
          selected_exercise: null,
          selected_class: classID,
          selected_learning_topic_code: String(draft.topic_code || "").trim().toUpperCase() || null,
          selected_difficulty: Number(values.get(`exercise_${exerciseIndex}_difficulty`) || draft.difficulty_level || 1),
          selected_status: "Published",
          english_title: locale === "en" ? editedTitle : (draft.en?.title || editedTitle),
          english_topic: locale === "en" ? editedTopic : (draft.en?.topic || editedTopic),
          english_questions: locale === "en" ? editedQuestions : (draft.en?.questions || editedQuestions),
          italian_title: locale === "it" ? editedTitle : (draft.it?.title || editedTitle),
          italian_topic: locale === "it" ? editedTopic : (draft.it?.topic || editedTopic),
          italian_questions: locale === "it" ? editedQuestions : (draft.it?.questions || editedQuestions),
        });
      }
      closeModal();
      await loadAccount();
      showToast(state.language === "it"
        ? `${exercises.length} esercizi confermati e assegnati alla classe.`
        : `${exercises.length} exercises confirmed and assigned to the class.`);
      state.view = "exercises";
      teacherExercisesView();
    } catch (error) {
      form.querySelector(".form-message").textContent = readableError(error, state.language === "it" ? "Impossibile assegnare la bozza." : "Unable to assign the draft.");
      setBusy(button, false);
    }
  });
}

export function importExercisesModal() {
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Importa CSV esercizi" : "Import exercises CSV"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="import-exercises-form" class="form-grid">
      <p style="margin:0;color:var(--muted);font-size:13px">${state.language === "it"
        ? "Funzione riservata all’amministratore. Gli esercizi importati entrano nella libreria globale BrainMath."
        : "Administrator-only function. Imported exercises are saved to the global BrainMath library."}</p>
      <a href="/sample-exercises.csv" download class="secondary full" style="text-align:center;text-decoration:none">${state.language === "it" ? "Scarica CSV di esempio" : "Download sample CSV"}</a>
      <a href="/template-domande-mancanti.csv" download class="secondary full" style="text-align:center;text-decoration:none">${state.language === "it" ? "Scarica template completo delle domande mancanti" : "Download full missing-question template"}</a>
      <label>${state.language === "it" ? "File CSV" : "CSV file"}<input name="csvFile" type="file" accept=".csv,text/csv"></label>
      <label>${state.language === "it" ? "Oppure incolla il CSV" : "Or paste the CSV"}<textarea name="csvText" rows="8" placeholder="materia,area_matematica,argomento,codice_esercizio,titolo_esercizio..."></textarea></label>
      <p style="margin:0;color:var(--muted);font-size:13px">${state.language === "it"
        ? "Una riga corrisponde a una domanda a scelta multipla. Lo stesso codice_esercizio aggiunge domande all’attività esistente; un codice nuovo crea Attività 2, 3 e successive. Non serve indicare tipologie tecniche né conoscere JSON."
        : "One row equals one multiple-choice question. The same exercise_code appends questions to the existing activity; a new code creates Activity 2, 3 and later. No technical type or JSON is required."}</p>
      <button class="primary full" type="submit">${state.language === "it" ? "Importa" : "Import"}</button>
      <p class="form-message"></p>
    </form>`);

  const form = document.querySelector("#import-exercises-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    const values = new FormData(form);
    setBusy(button, true);
    try {
      const file = form.elements.csvFile.files?.[0];
      const rawText = file ? await file.text() : String(values.get("csvText") || "");
      if (!rawText.trim()) {
        throw new Error(state.language === "it" ? "Carica un file CSV o incolla il contenuto." : "Upload a CSV file or paste its content.");
      }
      const rows = parseStructuredExerciseCSV(rawText);
      const { error } = await supabase.rpc("import_admin_exercises_batch", {
        selected_rows: rows
      });
      if (error) throw error;
      closeModal();
      await loadAdminStatistics();
      adminView();
      showToast(state.language === "it" ? "Import completato." : "Import completed.");
    } catch (error) {
      form.querySelector(".form-message").textContent = readableError(error, state.language === "it" ? "Import non riuscito." : "Import failed.");
      setBusy(button, false);
    }
  });
}
